import CheckIcon from "@mui/icons-material/CheckRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopyRounded";
import {
  Box,
  IconButton,
  type SxProps,
  type Theme,
  Tooltip,
  alpha,
} from "@mui/material";
import type { Schema } from "hast-util-sanitize";
import { type ComponentProps, useRef, useState } from "react";
import ReactMarkdown, {
  type Components,
  defaultUrlTransform,
} from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

// hast-util-sanitize exports PropertyDefinition from its index but the
// package's exports field doesn't surface it through the public entry,
// so we inline the structural type here. Matches the upstream signature
// at hast-util-sanitize/lib/index.d.ts.
type PropertyDef =
  | [string, ...Array<string | number | boolean | RegExp>]
  | string;
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import "highlight.js/styles/github-dark.css";
import "katex/dist/katex.min.css";

// react-markdown's default urlTransform only whitelists http(s)/irc/mailto/
// xmpp and strips everything else to "" — including `data:` URIs. Our
// `_render_finding` tool inlines its matplotlib PNG as
// `data:image/png;base64,...` inside `![alt](url)`, so the default
// transform was rendering those as broken images. Allow data:image/* (and
// only that — no `data:text/html`, no other schemes) and delegate to the
// upstream default for everything else.
function allowDataImageUrl(
  url: string,
  key: string,
  node: { tagName?: string },
): string {
  if (
    key === "src" &&
    node.tagName === "img" &&
    /^data:image\/[a-z0-9.+-]+;base64,/i.test(url)
  ) {
    return url;
  }
  return defaultUrlTransform(url);
}

// ── Raw-HTML hardening ────────────────────────────────────────────────────────
// Sonnet 4.6 sometimes rewrites markdown image syntax `![alt](data:...)` as
// raw HTML `<img src="data:..." alt="..."/>`. Without rehypeRaw, react-markdown
// escapes raw HTML to text → broken render. With rehypeRaw alone, raw HTML is
// allowed unfiltered → XSS surface. The right combination is rehypeRaw THEN
// rehypeSanitize against an allowlist schema.
//
// We extend the default rehype-sanitize schema (already conservative) to:
//   • allow the `data:` URL protocol on <img src> (default is http/https only,
//     so the matplotlib-PNG data URIs were being stripped wholesale even with
//     a regex constraint in place)
//   • restrict allowed src VALUES to http(s) and `data:image/*;base64,...`
//     so `data:text/html` and other dangerous schemes can't sneak through
//   • allow KaTeX's generated <span class="katex"> tree (without these
//     classes the math blocks render as bare text)
//   • allow rehype-highlight's `language-*` / `hljs` classes on <code>/<pre>
//
// Two checks are layered:
//   1. `protocols.src` — coarse scheme allowlist (gate-keeps `data:`)
//   2. `attributes.img` regex — fine-grained value pattern (keeps only
//      `data:image/*;base64,...`, rejects `data:text/html`, etc.)
// Both must agree for the src to survive sanitize.
const _markdownSchema: Schema = {
  ...defaultSchema,
  protocols: {
    ...(defaultSchema.protocols ?? {}),
    // Add `data` to the src protocol allowlist — original was ['http','https'].
    // The attributes regex below narrows this to data:image/* only.
    src: [...(defaultSchema.protocols?.src ?? ["http", "https"]), "data"],
  },
  attributes: {
    ...(defaultSchema.attributes ?? {}),
    img: [
      // Spread the default img attributes (aria-*, longDesc, etc.) but EXCLUDE
      // the bare `'src'` entry — that allows ANY src value with no constraint
      // and would make the regex below moot, letting `data:text/html` through.
      ...(((defaultSchema.attributes ?? {}).img as PropertyDef[]) ?? []).filter(
        (entry) => entry !== "src",
      ),
      // Restrict `src` VALUES — must match http(s) OR data:image/*. With the
      // bare 'src' filtered out above, this is the ONLY rule for the src
      // attribute, so `data:text/html`, `javascript:`, etc. are rejected.
      ["src", /^https?:\/\//, /^data:image\/[a-z0-9.+-]+;base64,/i],
      "alt",
      "title",
      "width",
      "height",
    ],
    code: [
      ...(((defaultSchema.attributes ?? {}).code as PropertyDef[]) ?? []),
      ["className", /^language-[A-Za-z0-9_-]+$/, "hljs"],
    ],
    pre: [
      ...(((defaultSchema.attributes ?? {}).pre as PropertyDef[]) ?? []),
      ["className", "hljs"],
    ],
    span: [
      ...(((defaultSchema.attributes ?? {}).span as PropertyDef[]) ?? []),
      // KaTeX wraps every typeset math block in <span class="katex …"> with
      // many sub-classes; widen the allowed values rather than enumerate.
      ["className", /^katex/, /^mord/, /^mrel/, /^mbin/, /^mop/, /^minner/],
      "style",
    ],
  },
  // KaTeX renders nested annotation tags; whitelist them alongside the
  // markdown defaults so the math tree survives sanitization.
  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    "math",
    "semantics",
    "mrow",
    "mn",
    "mo",
    "mi",
    "mtext",
    "msup",
    "msub",
    "msubsup",
    "mfrac",
    "msqrt",
    "mroot",
    "mover",
    "munder",
    "munderover",
    "annotation",
  ],
};

// LLM output regularly contains literal Unicode `Ω` (ohms) and `µ`
// (micro) inside `$…$` math blocks. KaTeX's default strict mode warns
// for every occurrence ("Unrecognized Unicode character") and renders
// `µ` without a glyph — silent in production but a warning storm in
// the console. Map the offending characters to their TeX equivalents
// via `macros` so they render as proper math glyphs, and downgrade
// strict mode so any other unmapped Unicode degrades silently instead
// of spamming.
const _katexOptions = {
  strict: "ignore" as const,
  macros: {
    Ω: "\\Omega",
    µ: "\\mu",
  },
};

function CodeBlock({ children, ...props }: ComponentProps<"pre">) {
  const preRef = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      const text = preRef.current?.textContent ?? "";
      if (!text) return;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        "&:hover .code-copy-btn, & .code-copy-btn:focus-visible": {
          opacity: 1,
        },
      }}
    >
      <pre ref={preRef} {...props}>
        {children}
      </pre>
      <Tooltip title={copied ? "Copied" : "Copy code"} placement="left">
        <IconButton
          className="code-copy-btn"
          size="small"
          onClick={handleCopy}
          aria-label="Copy code"
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            bgcolor: "surface.raised",
            border: "1px solid",
            borderColor: "border.default",
            opacity: 0,
            transition: "opacity 120ms",
            p: "4px",
            "&:hover": { bgcolor: "surface.raisedHover" },
          }}
        >
          {copied ? (
            <CheckIcon sx={{ fontSize: 14, color: "success.main" }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

const markdownComponents: Components = {
  pre: CodeBlock as Components["pre"],
};

const markdownSx: SxProps<Theme> = {
  "& p": {
    m: 0,
    mb: 1.25,
    color: "text.primary",
    fontSize: "0.9375rem",
    lineHeight: 1.65,
  },
  "& p:last-child": { mb: 0 },
  "& p:empty": { display: "none" },

  "& code": {
    backgroundColor: "surface.sunken",
    border: "1px solid",
    borderColor: "border.default",
    borderRadius: "4px",
    px: "5px",
    py: "1px",
    fontSize: "0.8125rem",
    color: "secondary.main",
    fontFamily: (t) => t.fonts.mono,
  },
  "& pre": {
    backgroundColor: "surface.sunken",
    border: "1px solid",
    borderColor: "border.default",
    borderRadius: 2,
    p: 1.75,
    overflowX: "auto",
    my: 1.5,
    "& code": {
      backgroundColor: "transparent",
      border: "none",
      p: 0,
      fontSize: "0.8125rem",
      color: "text.primary",
    },
  },

  "& table": {
    borderCollapse: "separate",
    borderSpacing: 0,
    width: "100%",
    fontSize: "0.8125rem",
    my: 1.25,
    display: "block",
    overflow: "auto",
    maxHeight: 480,
    border: "1px solid",
    borderColor: "border.default",
    borderRadius: 1.5,
  },
  "& th, & td": {
    borderBottom: "1px solid",
    borderColor: "border.default",
    px: 1.5,
    py: 0.85,
    color: "text.primary",
    textAlign: "left",
  },
  "& tr:last-child td": {
    borderBottom: "none",
  },
  "& th": {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "surface.raisedHover",
    color: "text.secondary",
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    // The sticky header needs its own bottom rule — the cell's
    // border-bottom doesn't render with the sticky paint layer
    // because separate-borders + sticky decouples them.
    boxShadow: (t) => `inset 0 -1px 0 ${t.palette.divider}`,
  },
  // Zebra: every other body row gets a faint tint so dense BOM-style
  // tables read in stripes instead of a wall of cells. Targets only
  // tbody rows so the sticky header isn't striped.
  "& tbody tr:nth-of-type(even) td": {
    backgroundColor: (t: Theme) => alpha(t.palette.primary.main, 0.025),
  },
  // Hover: tint the row + add a 2px amber rule on the leading cell —
  // the same pin-1 ember signal the rest of the brand uses for
  // "active / pointer here". Implemented as a box-shadow inset so it
  // doesn't shift the cell's intrinsic width.
  "& tbody tr:hover td": {
    backgroundColor: "accent.softer",
  },
  "& tbody tr:hover td:first-of-type": {
    boxShadow: (t: Theme) => `inset 2px 0 0 ${t.palette.warning.main}`,
  },

  "& ul, & ol": { pl: 2.75, my: 0.75, mb: 1.25 },
  "& li": {
    color: "text.primary",
    fontSize: "0.9375rem",
    lineHeight: 1.65,
    mb: 0.25,
  },

  "& h1": {
    color: "text.primary",
    fontWeight: 700,
    mt: 2,
    mb: 1,
    fontSize: "1.25rem",
    letterSpacing: "-0.01em",
  },
  "& h2": {
    color: "text.primary",
    fontWeight: 700,
    mt: 1.75,
    mb: 0.75,
    fontSize: "1.125rem",
    letterSpacing: "-0.005em",
  },
  "& h3, & h4": {
    color: "text.primary",
    fontWeight: 600,
    mt: 1.25,
    mb: 0.5,
    fontSize: "1rem",
  },

  "& blockquote": {
    borderLeft: "3px solid",
    borderColor: "primary.main",
    pl: 1.75,
    ml: 0,
    my: 1.25,
    color: "text.secondary",
    fontStyle: "italic",
  },
  "& a": {
    color: "primary.light",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  "& strong": { color: "text.primary", fontWeight: 600 },
  "& hr": {
    border: "none",
    borderTop: "1px solid",
    borderColor: "border.default",
    my: 2,
  },
};

interface MarkdownRendererProps {
  children: string;
  sx?: SxProps<Theme>;
}

/**
 * Shared markdown renderer used by ChatMessage and StreamingMessage.
 * Owns all prose styling so the two callers cannot drift apart.
 */
export function MarkdownRenderer({ children, sx }: MarkdownRendererProps) {
  return (
    <Box sx={[markdownSx, ...(Array.isArray(sx) ? sx : [sx])]}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        // Order matters: rehypeRaw must run BEFORE rehypeSanitize so raw
        // HTML is parsed into the AST and then sanitized against the
        // schema. rehypeKatex / rehypeHighlight come after sanitize so
        // their generated nodes aren't accidentally stripped — those
        // classes are explicitly whitelisted in `_markdownSchema` above.
        rehypePlugins={[
          rehypeRaw,
          [rehypeSanitize, _markdownSchema],
          [rehypeKatex, _katexOptions],
          rehypeHighlight,
        ]}
        components={markdownComponents}
        urlTransform={allowDataImageUrl}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
}
